import { getTokenClientes } from '../Store/storages/AuthenticationStorage';
import { apiClientes } from '../config/api';
import { TranscricaoDetalhe } from '../interfaces/entities/Transcricao/TranscricaoDetalhe';
import { TranscricaoHistoricoStatus } from '../interfaces/entities/Transcricao/TranscricaoHistoricoStatus';
import { TranscricaoListaItem } from '../interfaces/entities/Transcricao/TranscricaoListaItem';
import {
  TranscricaoHistoricoItem,
  TranscricaoItemDetalhe,
} from '../interfaces/entities/Transcricao/TranscricaoRevisao';
import { TranscricaoResourceToken } from '../interfaces/entities/Transcricao/transcricaoResourceToken';


const RECUPERAR_LISTA_PATH = '/transcricao/recuperarlistatranscricao';
const RECUPERAR_TRANSCRICAO_PATH = '/transcricao/RecuperarTranscricao';
const RECUPERAR_TRANSCRICAO_DET_PATH = '/transcricao/RecuperarTranscricaoDet';
const CRIAR_TRANSCRICAO_PATH = '/transcricao/criartranscricao';
const CRIAR_TRANSCRICAO_ANEXO_PATH = '/transcricao/CriarAnexoTranscricao';
const PROCESSAR_TRANSCRICAO_IACEA_PATH = '/transcricao/ProcessarTranscricaoIACEA';
const VALIDAR_DOCUMENTO_LEGIBILIDADE_IACEA_PATH = '/transcricao/ValidarDocumentoLegibilidadeIACEA';
const RECUPERAR_HISTORICO_PATH = '/transcricao/recuperartranscricaohistorico';
const RECUPERAR_HISTORICO_STATUS_PATH = '/transcricao/RecuperarTranscricaoHistoricoStatus';
const RECUPERAR_HISTORICO_SUMARIO_PATH = '/Transcricao/RecuperarTranscricaoHistoricoSumario';
const RECUPERAR_ITEM_PATH = '/transcricao/recuperartranscricaoitem';
const OBTER_RASCUNHO_POR_BENEF_PATH = '/transcricao/ObterRascunhoTranscricaoPorBenef';
const ATUALIZAR_ITEM_TRANSCRICAO = '/transcricao/AtualizarTranscricaoItem';
const ATUALIZAR_TRANSCRICAO_PATH = '/transcricao/AtualizarTranscricao';
const ATUALIZAR_TIPO_ATENDIMENTO_TRANSCRICAO_PATH = '/transcricao/AtualizarTipoAtendimentoTranscricao';
const OBTER_DESCRICAO_ITEM_PATH = '/transcricao/ObterDescricaoItem';
const RECUPERAR_LISTA_UF_CONSELHO_PROFISSIONAL_PATH = '/transcricao/RecuperarListaUfConselhoProfissional';
const RECUPERAR_LISTA_CONSELHO_PROFISSIONAL_PATH = '/transcricao/RecuperarListaConselhoProfissional';
const ENTREGAR_TRANSCRICAO_PATH = '/transcricao/EntregarTranscricao';

export const TRANSCRICAO_SEM_PERMISSAO_MESSAGE = 'Sem permissão para acessar este recurso.';

export type TranscricaoServiceResult<T> =
  | { ok: true; data: T }
  | { ok: false; message: string };

export type TranscricaoActionResult =
  | { ok: true }
  | { ok: false; message: string };

const getAuthConfig = async () => ({
  headers: { Authorization: `Bearer ${await getTokenClientes()}` },
});

const failServiceResult = <T>(error: unknown, fallback: string): TranscricaoServiceResult<T> => ({
  ok: false,
  message: extractHttpErrorMessage(error, fallback),
});

const failActionResult = (error: unknown, fallback: string): TranscricaoActionResult => ({
  ok: false,
  message: extractHttpErrorMessage(error, fallback),
});

const normalizeListaResponse = (data: unknown): TranscricaoListaItem[] | null => {
  if (Array.isArray(data)) {
    return data as TranscricaoListaItem[];
  }
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const lista = record.lista ?? record.itens ?? record.dados ?? record.data;
    if (Array.isArray(lista)) {
      return lista as TranscricaoListaItem[];
    }
  }
  return null;
};

const parseNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  if (Array.isArray(value) && value.length > 0) {
    return parseNumericId(value[0]);
  }

  return null;
};

const isNestedTranscricaoPayload = (record: Record<string, unknown>): boolean =>
  Boolean(
    pickStringByNormalizedKey(record, 'idtranscricao', 'idTranscricao', 'IdTranscricao')
    || pickStringByNormalizedKey(record, 'codprotocoloans', 'codProtocoloans', 'CodProtocoloans')
    || pickStringByNormalizedKey(record, 'datacadastro', 'dataCadastro', 'DataCadastro')
    || pickStringByNormalizedKey(record, 'codstatustranscricao', 'codStatusTranscricao', 'CodStatusTranscricao')
  );

const resolveTranscricaoPayload = (data: unknown): Record<string, unknown> | null => {
  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  const nestedRaw = record.dados ?? record.data ?? record.resultado ?? record.retorno;

  if (nestedRaw && typeof nestedRaw === 'object' && !Array.isArray(nestedRaw)) {
    const nested = nestedRaw as Record<string, unknown>;
    if (isNestedTranscricaoPayload(nested)) {
      return { ...record, ...nested };
    }
  }

  return record;
};

const pickField = <T>(
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string,
): T | undefined => {
  const value = record[camelKey] ?? record[pascalKey];
  return value as T | undefined;
};

const normalizeRecordKey = (key: string): string =>
  key.replace(/[_\s-]/g, '').toLowerCase();

const scalarToString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'object') return undefined;

  const text = String(value).trim();
  if (!text || text === '[object Object]') return undefined;
  return text;
};

const pickString = (
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string,
  ...extraKeys: string[]
): string | undefined => {
  const keys = [camelKey, pascalKey, ...extraKeys];
  for (const key of keys) {
    const text = scalarToString(record[key]);
    if (text) return text;
  }
  return undefined;
};

const pickStringByNormalizedKey = (
  record: Record<string, unknown>,
  normalizedKey: string,
  ...exactKeys: string[]
): string | undefined => {
  const fromExact = pickString(record, exactKeys[0], exactKeys[1] ?? exactKeys[0], ...exactKeys.slice(2));
  if (fromExact) return fromExact;

  const target = normalizeRecordKey(normalizedKey);
  for (const [key, value] of Object.entries(record)) {
    if (normalizeRecordKey(key) !== target) continue;
    const text = scalarToString(value);
    if (text) return text;
  }

  return undefined;
};

const mergeTranscricaoResponseRecord = (data: unknown): Record<string, unknown> | null => {
  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  const payload = resolveTranscricaoPayload(data);

  if (!payload) return record;
  if (payload === record) return record;

  return { ...record, ...payload };
};

const mapRecordToTranscricaoHistoricoStatus = (
  record: Record<string, unknown>,
): TranscricaoHistoricoStatus | null => {
  const codStatusTranscricao = pickStringByNormalizedKey(
    record,
    'codstatustranscricao',
    'codStatusTranscricao',
    'CodStatusTranscricao',
  );

  if (!codStatusTranscricao) return null;

  return {
    codStatusTranscricao,
    descrStatusTranscricao: pickStringByNormalizedKey(
      record,
      'descrstatustranscricao',
      'descrStatusTranscricao',
      'DescrStatusTranscricao',
    ),
    dataOperacao: pickStringByNormalizedKey(
      record,
      'dataoperacao',
      'dataOperacao',
      'DataOperacao',
    ),
  };
};

const normalizeHistoricoStatusResponse = (data: unknown): TranscricaoHistoricoStatus | null => {
  const merged = mergeTranscricaoResponseRecord(data);
  if (!merged) return null;

  return mapRecordToTranscricaoHistoricoStatus(merged);
};

const normalizeHistoricoSumarioResponse = (data: unknown): TranscricaoHistoricoStatus[] => {
  const lista = resolveGenericLista(data);
  if (!lista) return [];

  return lista
    .map((item) => (item && typeof item === 'object'
      ? mapRecordToTranscricaoHistoricoStatus(item as Record<string, unknown>)
      : null))
    .filter((item): item is TranscricaoHistoricoStatus => item !== null);
};

const pickNumber = (
  record: Record<string, unknown>,
  camelKey: string,
  pascalKey: string,
): number | undefined => {
  const parsed = parseNumericId(pickField<unknown>(record, camelKey, pascalKey));
  return parsed ?? undefined;
};

const mapRecordToTranscricaoDetalhe = (
  record: Record<string, unknown>,
): TranscricaoDetalhe => ({
  idTranscricao: pickStringByNormalizedKey(record, 'idtranscricao', 'idTranscricao', 'IdTranscricao'),
  codStatusTranscricao: pickStringByNormalizedKey(
    record,
    'codstatustranscricao',
    'codStatusTranscricao',
    'CodStatusTranscricao',
  ),
  descrStatusTranscricao: pickStringByNormalizedKey(
    record,
    'descrstatustranscricao',
    'descrStatusTranscricao',
    'DescrStatusTranscricao',
  ),
  idUsrSist: pickNumber(record, 'idUsrSist', 'IdUsrSist'),
  codTipoTranscricao: pickStringByNormalizedKey(
    record,
    'codtipotranscricao',
    'codTipoTranscricao',
    'CodTipoTranscricao',
    'codtiposervicotranscricao',
    'codTipoServicoTranscricao',
    'CodTipoServicoTranscricao',
  ),
  idUsrContr: pickNumber(record, 'idUsrContr', 'IdUsrContr'),
  codAntigoUsr: pickString(record, 'codAntigoUsr', 'CodAntigoUsr'),
  nomeBeneficiario: pickString(record, 'nomeBeneficiario', 'NomeBeneficiario'),
  nomeSolicitanteInformado: pickString(record, 'nomeSolicitanteInformado', 'NomeSolicitanteInformado'),
  codigoConselhoInformado: pickString(record, 'codigoConselhoInformado', 'CodigoConselhoInformado'),
  numeroConselhoInformado: pickString(record, 'numeroConselhoInformado', 'NumeroConselhoInformado'),
  idUfConselhoInformado:
    pickNumber(record, 'idUfConselhoInformado', 'IdUfConselhoInformado')
    ?? pickNumber(record, 'idUf', 'IdUf'),
  indAtendSuspenso: pickString(record, 'indAtendSuspenso', 'IndAtendSuspenso'),
  codStatusUsr: pickString(record, 'codStatusUsr', 'CodStatusUsr'),
  siglaUfConselhoInformado: pickString(record, 'siglaUfConselhoInformado', 'SiglaUfConselhoInformado'),
  localInformado: pickString(record, 'localInformado', 'LocalInformado'),
  dataAceite: pickStringByNormalizedKey(record, 'dataaceite', 'dataAceite', 'DataAceite'),
  dataCadastro: pickStringByNormalizedKey(record, 'datacadastro', 'dataCadastro', 'DataCadastro'),
  dataCompetenciaArquivo: pickStringByNormalizedKey(
    record,
    'datacompetenciaarquivo',
    'dataCompetenciaArquivo',
    'DataCompetenciaArquivo',
  ),
  indProcessoJudicial: pickString(record, 'indProcessoJudicial', 'IndProcessoJudicial'),
  idDiagCid: pickNumber(record, 'idDiagCid', 'IdDiagCid'),
  codigoDiagCid: pickStringByNormalizedKey(
    record,
    'codigodiagcid',
    'codigoDiagCid',
    'CodigoDiagCid',
    'CODIGODIAGCID',
  ),
  descrDiagCid: pickStringByNormalizedKey(
    record,
    'descrdiagcid',
    'descrDiagCid',
    'DescrDiagCid',
    'DESCRDIAGCID',
  ),
  codDescrDiagCid: pickStringByNormalizedKey(
    record,
    'coddescrdiagcid',
    'codDescrDiagCid',
    'CodDescrDiagCid',
    'CODDESCRDIAGCID',
  ),
  observacao: pickString(record, 'observacao', 'Observacao'),
  codNecessidAtend: pickString(record, 'codNecessidAtend', 'CodNecessidAtend'),
  codTipoNecessidAtend: pickString(record, 'codTipoNecessidAtend', 'CodTipoNecessidAtend'),
  idProtocoloAns: pickNumber(record, 'idProtocoloAns', 'IdProtocoloAns'),
  codProtocoloans: pickStringByNormalizedKey(
    record,
    'codprotocoloans',
    'codProtocoloans',
    'CodProtocoloans',
  ),
  numeroAutorizacao: pickStringByNormalizedKey(
    record,
    'numeroautorizacao',
    'numeroAutorizacao',
    'NumeroAutorizacao',
  ),
  idSolicitante: pickNumber(record, 'idSolicitante', 'IdSolicitante'),
  idLocalAtendimento: pickNumber(record, 'idLocalAtendimento', 'IdLocalAtendimento'),
  codLocalAtendimento: pickString(record, 'codLocalAtendimento', 'CodLocalAtendimento'),
  descricaoLocalAtendimento: pickString(record, 'descricaoLocalAtendimento', 'DescricaoLocalAtendimento'),
  nomeSolicitante: pickString(record, 'nomeSolicitante', 'NomeSolicitante'),
  motivoTranscricao: pickString(record, 'motivoTranscricao', 'MotivoTranscricao'),
  loginAuditor: pickString(record, 'loginAuditor', 'LoginAuditor'),
  codTipoPrioridade: pickString(record, 'codTipoPrioridade', 'CodTipoPrioridade'),
  motivoPrioridade: pickString(record, 'motivoPrioridade', 'MotivoPrioridade'),
  indCooperado: pickString(record, 'indCooperado', 'IndCooperado'),
});

const extractDetalheRecord = (data: unknown): Record<string, unknown> | null => {
  if (!data || typeof data !== 'object') return null;

  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    const first = data[0];
    return first && typeof first === 'object' ? (first as Record<string, unknown>) : null;
  }

  const root = data as Record<string, unknown>;
  const rootLista = root.lista ?? root.itens;
  if (Array.isArray(rootLista) && rootLista.length > 0) {
    const first = rootLista[0];
    if (first && typeof first === 'object') {
      return first as Record<string, unknown>;
    }
  }

  const payload = resolveTranscricaoPayload(data);
  if (!payload) return null;

  if (Array.isArray(payload)) {
    if (payload.length === 0) return null;
    const first = payload[0];
    return first && typeof first === 'object' ? (first as Record<string, unknown>) : null;
  }

  const nestedLista = payload.lista ?? payload.itens;
  if (Array.isArray(nestedLista) && nestedLista.length > 0) {
    const first = nestedLista[0];
    if (first && typeof first === 'object') {
      return first as Record<string, unknown>;
    }
  }

  return payload;
};

const flattenDetalheRecord = (record: Record<string, unknown>): Record<string, unknown> => {
  const nestedKeys = ['diagnostico', 'diagCid', 'cid', 'transcricao'];
  let merged: Record<string, unknown> = { ...record };

  nestedKeys.forEach((key) => {
    const nested = record[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      merged = { ...merged, ...(nested as Record<string, unknown>) };
    }
  });

  return merged;
};

const hasTranscricaoDetalheContent = (detalhe: TranscricaoDetalhe): boolean =>
  Boolean(
    detalhe.idTranscricao?.trim()
    || detalhe.codProtocoloans?.trim()
    || detalhe.dataCadastro?.trim()
    || detalhe.codStatusTranscricao?.trim()
    || detalhe.nomeBeneficiario?.trim()
  );

const normalizeDetalheResponse = (data: unknown): TranscricaoDetalhe | null => {
  const merged = mergeTranscricaoResponseRecord(data);
  if (!merged) return null;

  const record = extractDetalheRecord(merged) ?? merged;
  const detalhe = mapRecordToTranscricaoDetalhe(flattenDetalheRecord(record));

  if (!hasTranscricaoDetalheContent(detalhe)) {
    return null;
  }

  return detalhe;
};

const mapRecordToTranscricaoItemDetalhe = (
  record: Record<string, unknown>,
): TranscricaoItemDetalhe => ({
  idTranscricaoItem: pickString(record, 'idTranscricaoItem', 'IdTranscricaoItem') ?? '',
  idTranscricao: pickString(record, 'idTranscricao', 'IdTranscricao') ?? '',
  codAntigoUsr: pickString(record, 'codAntigoUsr', 'CodAntigoUsr'),
  tipoProdMed: pickString(record, 'tipoProdMed', 'TipoProdMed') ?? '',
  codigo: pickString(record, 'codigo', 'Codigo') ?? '',
  descricao: pickString(record, 'descricao', 'Descricao') ?? '',
  quantidade: pickNumber(record, 'quantidade', 'Quantidade') ?? 0,
  numeroGuia: pickNumber(record, 'numeroGuia', 'NumeroGuia'),
  idUsrSist: pickNumber(record, 'idUsrSist', 'IdUsrSist'),
  nomeUsrSist: pickString(record, 'nomeUsrSist', 'NomeUsrSist'),
  login: pickString(record, 'login', 'Login'),
  dataOperacao: pickString(record, 'dataOperacao', 'DataOperacao'),
  codProtocoloans: pickString(record, 'codProtocoloans', 'CodProtocoloans'),
});

const mapRecordToTranscricaoHistoricoItem = (
  record: Record<string, unknown>,
): TranscricaoHistoricoItem => ({
  idTranscricaoHistorico: pickString(record, 'idTranscricaoHistorico', 'IdTranscricaoHistorico') ?? '',
  idTranscricao: pickString(record, 'idTranscricao', 'IdTranscricao') ?? '',
  codAntigoUsr: pickString(record, 'codAntigoUsr', 'CodAntigoUsr'),
  tipoStatus: pickString(record, 'tipoStatus', 'TipoStatus') ?? '',
  observacao: pickString(record, 'observacao', 'Observacao'),
  idUsrtSist: pickNumber(record, 'idUsrtSist', 'IdUsrtSist'),
  nomeUsrSist: pickString(record, 'nomeUsrSist', 'NomeUsrSist'),
  login: pickString(record, 'login', 'Login'),
  dataOperacao: pickString(record, 'dataOperacao', 'DataOperacao'),
  codProtocoloAns: pickString(record, 'codProtocoloAns', 'CodProtocoloAns'),
});

const normalizeItensResponse = (data: unknown): TranscricaoItemDetalhe[] => {
  const lista = resolveGenericLista(data);
  if (!lista) return [];

  return lista
    .map((item) => (item && typeof item === 'object'
      ? mapRecordToTranscricaoItemDetalhe(item as Record<string, unknown>)
      : null))
    .filter((item): item is TranscricaoItemDetalhe => item !== null && !!item.idTranscricaoItem);
};

const normalizeHistoricoResponse = (data: unknown): TranscricaoHistoricoItem[] => {
  const lista = resolveGenericLista(data);
  if (!lista) return [];

  return lista
    .map((item) => (item && typeof item === 'object'
      ? mapRecordToTranscricaoHistoricoItem(item as Record<string, unknown>)
      : null))
    .filter((item): item is TranscricaoHistoricoItem => item !== null);
};

const buildConsultaTranscricaoBody = (idTranscricao: TranscricaoResourceToken) => ({
  IdTranscricao: idTranscricao,
});

export const recuperarTranscricao = async (
  idTranscricao: TranscricaoResourceToken,
): Promise<TranscricaoServiceResult<TranscricaoDetalhe>> => {
  const fallbackMessage =
    'Não foi possível carregar os detalhes da transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      RECUPERAR_TRANSCRICAO_PATH,
      buildConsultaTranscricaoBody(idTranscricao),
      await getAuthConfig(),
    );
    const detalhe = normalizeDetalheResponse(response.data);
    if (!detalhe) {
      return { ok: false, message: fallbackMessage };
    }
    return { ok: true, data: detalhe };
  } catch (error) {
    console.log({ error });
    return failServiceResult(error, fallbackMessage);
  }
};

export const recuperarTranscricaoDet = async (
  idTranscricao: TranscricaoResourceToken,
): Promise<TranscricaoServiceResult<TranscricaoDetalhe>> => {
  const fallbackMessage =
    'Não foi possível carregar os detalhes da transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      RECUPERAR_TRANSCRICAO_DET_PATH,
      buildConsultaTranscricaoBody(idTranscricao),
      await getAuthConfig(),
    );
    const detalhe = normalizeDetalheResponse(response.data);
    if (!detalhe) {
      return { ok: false, message: fallbackMessage };
    }
    return { ok: true, data: detalhe };
  } catch (error) {
    console.log({ error });
    return failServiceResult(error, fallbackMessage);
  }
};

export const recuperarListaTranscricao = async (): Promise<
  TranscricaoServiceResult<TranscricaoListaItem[]>
> => {
  const fallbackMessage =
    'Não foi possível carregar as transcrições. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      RECUPERAR_LISTA_PATH,
      undefined,
      await getAuthConfig(),
    );
    return { ok: true, data: normalizeListaResponse(response.data) ?? [] };
  } catch (error) {
    console.log({ error });
    return failServiceResult(error, fallbackMessage);
  }
};

const resolveGenericLista = (data: unknown): unknown[] | null => {
  const lista = normalizeListaResponse(data);
  if (lista) return lista;

  if (Array.isArray(data)) return data;

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const nested = record.resultado ?? record.retorno;
    if (Array.isArray(nested)) return nested;
  }

  return null;
};

const normalizeGenericLista = <T>(
  data: unknown,
  mapper: (record: Record<string, unknown>) => T | null,
): T[] | null => {
  const lista = resolveGenericLista(data);
  if (!lista) return null;

  return lista
    .map((item) => (item && typeof item === 'object' ? mapper(item as Record<string, unknown>) : null))
    .filter((item): item is T => item !== null);
};

const mapRecordToUfConselhoProfissional = (
  record: Record<string, unknown>,
): UfConselhoProfissionalItem | null => {
  const idUfConselhoInformado =
    pickNumber(record, 'idUf', 'IdUf')
    ?? pickNumber(record, 'idUfConselhoInformado', 'IdUfConselhoInformado');

  const sigla =
    pickString(record, 'sigla', 'Sigla')
    ?? pickString(record, 'siglaUf', 'SiglaUf')
    ?? pickString(record, 'siglaUfConselhoInformado', 'SiglaUfConselhoInformado');

  if (idUfConselhoInformado === undefined || !sigla) return null;

  return { idUfConselhoInformado, sigla };
};

const mapRecordToConselhoProfissional = (
  record: Record<string, unknown>,
): ConselhoProfissionalItem | null => {
  const codigo =
    pickString(record, 'codigo', 'Codigo')
    ?? pickString(record, 'codigoConselho', 'CodigoConselho')
    ?? pickString(record, 'codigoConselhoInformado', 'CodigoConselhoInformado');

  if (!codigo) return null;

  const descricao =
    pickString(record, 'descricao', 'Descricao')
    ?? pickString(record, 'descrConselho', 'DescrConselho')
    ?? codigo;

  return { codigo, descricao };
};

export const recuperarListaUfConselhoProfissional = async (): Promise<UfConselhoProfissionalItem[] | null> => {
  try {
    const response = await apiClientes.post(
      RECUPERAR_LISTA_UF_CONSELHO_PROFISSIONAL_PATH,
      {},
      {
        headers: { Authorization: `Bearer ${await getTokenClientes()}` },
      },
    );
    return normalizeGenericLista(response.data, mapRecordToUfConselhoProfissional);
  } catch (error) {
    console.log({ error });
    return null;
  }
};

export const recuperarListaConselhoProfissional = async (): Promise<ConselhoProfissionalItem[] | null> => {
  try {
    const response = await apiClientes.post(
      RECUPERAR_LISTA_CONSELHO_PROFISSIONAL_PATH,
      {},
      {
        headers: { Authorization: `Bearer ${await getTokenClientes()}` },
      },
    );
    return normalizeGenericLista(response.data, mapRecordToConselhoProfissional);
  } catch (error) {
    console.log({ error });
    return null;
  }
};


export interface AtualizaItemTranscricaoRequest {
  idTranscricao: TranscricaoResourceToken;
  idTranscricaoItem: TranscricaoResourceToken;
  tipoProdMed: string;
  quantidade: number;
  codigo: string;
  descricao: string;
}

export interface AtualizarTranscricaoRequest {
  idTranscricao: TranscricaoResourceToken;
  localAtendimentoInformado: string;
  nomeSolicitanteInformado: string;
  codigoConselhoInformado: string;
  numeroConselhoInformado: string;
  idUfConselhoInformado: number;
  idDiagCid?: number;
  codTipoNecessidAtend: string;
  observacao: string;
}

export interface AtualizarTipoAtendimentoTranscricaoRequest {
  idTranscricao: TranscricaoResourceToken;
  codTipoServicoTranscricao: string;
}

export interface ObterDescricaoItemRequest {
  codigo: string;
  tipoProdMed: string;
}

export interface UfConselhoProfissionalItem {
  idUfConselhoInformado: number;
  sigla: string;
}

export interface ConselhoProfissionalItem {
  codigo: string;
  descricao: string;
}

interface ObterDescricaoItemResponse {
  descricao?: string;
}

export type AtualizarItemTranscricaoResult =
  | { ok: true; idTranscricao: TranscricaoResourceToken }
  | { ok: false; message: string };

export interface CriarTranscricaoRequest {
  dataAceite: string;
}

export type CriarTranscricaoResult =
  | { ok: true; idTranscricao: TranscricaoResourceToken }
  | { ok: false; message: string };

export interface CriarTranscricaoAnexoRequest {
  idTranscricao: TranscricaoResourceToken;
  nomeArquivo: string;
  conteudo: string;
  contentType: string;
}

export interface ProcessarTranscricaoIACEARequest {
  idTranscricao: TranscricaoResourceToken;
}

export type ProcessarTranscricaoIACEAResult =
  | {
      ok: true;
      idTranscricao: TranscricaoResourceToken;
      codStatusTranscricao: string;
      descrStatusTranscricao?: string;
    }
  | { ok: false; message: string };

export interface ValidarDocumentoLegibilidadeIACEARequest {
  data: string;
  contentType: string;
  nomeArquivo: string;
}

export type ValidarDocumentoLegibilidadeIACEAResult =
  | { ok: true; arquivoValido: boolean; crm?: string | null }
  | { ok: false; message: string };

export interface EntregarTranscricaoRequest {
  idTranscricao: TranscricaoResourceToken;
  obs: string;
}

export type EntregarTranscricaoResult =
  | { ok: true }
  | { ok: false; message: string };

const extractValidationMessages = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  const messages: string[] = [];

  const errors = record.errors;
  if (errors && typeof errors === 'object') {
    Object.values(errors as Record<string, unknown>).forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === 'string' && item.trim()) {
            messages.push(item);
          }
        });
      }
    });
  }

  Object.values(record).forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === 'string' && item.trim()) {
          messages.push(item);
        }
      });
    }
  });

  const uniqueMessages = Array.from(new Set(messages));
  return uniqueMessages.length > 0 ? uniqueMessages.join(' ') : null;
};

const extractIdTranscricaoToken = (data: unknown): TranscricaoResourceToken | null => {
  const payload = resolveTranscricaoPayload(data);
  if (!payload) return null;

  const idRaw =
    payload.idTranscricao
    ?? payload.IdTranscricao
    ?? payload.id
    ?? payload.Id;

  if (idRaw === null || idRaw === undefined) return null;
  const text = String(idRaw).trim();
  return text || null;
};

const extractHttpErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as {
    response?: { status?: number; data?: unknown };
  };

  const status = axiosError.response?.status;
  if (status === 401) {
    return 'Sessão expirada. Faça login novamente.';
  }
  if (status === 403) {
    return TRANSCRICAO_SEM_PERMISSAO_MESSAGE;
  }
  if (status === 404) {
    return 'Recurso não encontrado.';
  }

  return buildCriarTranscricaoErrorMessage(axiosError.response?.data, fallback);
};

const buildCriarTranscricaoErrorMessage = (data: unknown, fallback: string): string => {
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const directMessage = record.message ?? record.mensagem ?? record.error ?? record.erro;
    if (typeof directMessage === 'string' && directMessage.trim()) {
      return directMessage;
    }
  }

  return extractValidationMessages(data) ?? fallback;
};

export const criarTranscricao = async (
  payload: CriarTranscricaoRequest,
): Promise<CriarTranscricaoResult> => {
  const fallbackMessage = 'Não foi possível criar a transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      CRIAR_TRANSCRICAO_PATH,
      {
        DataAceite: payload.dataAceite,
      },
      {
        headers: { Authorization: `Bearer ${await getTokenClientes()}` },
      },
    );

    const idTranscricao = extractIdTranscricaoToken(response.data);
    if (idTranscricao !== null) {
      return { ok: true, idTranscricao };
    }

    return {
      ok: false,
      message: buildCriarTranscricaoErrorMessage(response.data, fallbackMessage),
    };
  } catch (error: unknown) {
    console.log({ error });
    return {
      ok: false,
      message: extractHttpErrorMessage(error, fallbackMessage),
    };
  }
};

const buildEntregarTranscricaoBody = (payload: EntregarTranscricaoRequest) => ({
  IdTranscricao: payload.idTranscricao,
  obs: payload.obs,
});

export const entregarTranscricao = async (
  payload: EntregarTranscricaoRequest,
): Promise<EntregarTranscricaoResult> => {
  const fallbackMessage = 'Não foi possível entregar a transcrição. Tente novamente em instantes.';

  try {
    await apiClientes.post(
      ENTREGAR_TRANSCRICAO_PATH,
      buildEntregarTranscricaoBody(payload),
      await getAuthConfig(),
    );

    return { ok: true };
  } catch (error: unknown) {
    console.log({ error });
    return {
      ok: false,
      message: extractHttpErrorMessage(error, fallbackMessage),
    };
  }
};

const buildAtualizarItemBody = (payload: AtualizaItemTranscricaoRequest) => ({
  IdTranscricao: payload.idTranscricao,
  IdTranscricaoItem: payload.idTranscricaoItem,
  TipoProdMed: payload.tipoProdMed,
  Quantidade: payload.quantidade,
  Codigo: payload.codigo,
  Descricao: payload.descricao,
});

export const AtualizaItemTranscricao = async (
  payload: AtualizaItemTranscricaoRequest,
): Promise<TranscricaoActionResult> => {
  const fallbackMessage =
    'Não foi possível atualizar o procedimento. Tente novamente em instantes.';

  try {
    await apiClientes.post(
      ATUALIZAR_ITEM_TRANSCRICAO,
      buildAtualizarItemBody(payload),
      await getAuthConfig(),
    );
    return { ok: true };
  } catch (error) {
    console.log({ error });
    return failActionResult(error, fallbackMessage);
  }
};

const buildAtualizarTranscricaoBody = (payload: AtualizarTranscricaoRequest) => ({
  IdTranscricao: payload.idTranscricao,
  LocalAtendimentoInformado: payload.localAtendimentoInformado,
  NomeSolicitanteInformado: payload.nomeSolicitanteInformado,
  CodigoConselhoInformado: payload.codigoConselhoInformado,
  NumeroConselhoInformado: payload.numeroConselhoInformado,
  IdUfConselhoInformado: payload.idUfConselhoInformado,
  IdDiagCid: payload.idDiagCid,
  CodTipoNecessidAtend: payload.codTipoNecessidAtend,
  Observacao: payload.observacao ?? '',
});

export const atualizarTranscricao = async (
  payload: AtualizarTranscricaoRequest,
): Promise<TranscricaoActionResult> => {
  const fallbackMessage =
    'Não foi possível atualizar os dados do profissional. Tente novamente em instantes.';

  try {
    await apiClientes.post(
      ATUALIZAR_TRANSCRICAO_PATH,
      buildAtualizarTranscricaoBody(payload),
      await getAuthConfig(),
    );
    return { ok: true };
  } catch (error) {
    console.log({ error });
    return failActionResult(error, fallbackMessage);
  }
};

export const atualizarTipoAtendimentoTranscricao = async (
  payload: AtualizarTipoAtendimentoTranscricaoRequest,
): Promise<boolean> => {
  try {
    await apiClientes.post(
      ATUALIZAR_TIPO_ATENDIMENTO_TRANSCRICAO_PATH,
      {
        IdTranscricao: payload.idTranscricao,
        CodTipoServicoTranscricao: payload.codTipoServicoTranscricao,
      },
      await getAuthConfig(),
    );
    return true;
  } catch (error) {
    console.log({ error });
    return false;
  }
};

export const obterDescricaoItem = async (
  payload: ObterDescricaoItemRequest,
): Promise<string | null> => {
  try {
    const response = await apiClientes.post(
      OBTER_DESCRICAO_ITEM_PATH,
      {
        codigo: payload.codigo,
        tipoProdMed: payload.tipoProdMed,
      },
      {
        headers: { Authorization: `Bearer ${await getTokenClientes()}` },
      },
    );

    const data = response.data;
    if (!Array.isArray(data) || data.length === 0) return null;

    const firstItem = data[0] as ObterDescricaoItemResponse;
    const descricao = firstItem?.descricao?.trim();
    return descricao || null;
  } catch (error) {
    console.log({ error });
    return null;
  }
};

const buildCriarTranscricaoAnexoBody = (payload: CriarTranscricaoAnexoRequest) => ({
  IdTranscricao: payload.idTranscricao,
  NomeArquivo: payload.nomeArquivo,
  Conteudo: payload.conteudo,
  ContentType: payload.contentType,
});

export const criarTranscricaoAnexo = async (
  payload: CriarTranscricaoAnexoRequest,
): Promise<TranscricaoActionResult> => {
  const fallbackMessage = 'Não foi possível salvar o anexo. Tente novamente em instantes.';

  try {
    await apiClientes.post(
      CRIAR_TRANSCRICAO_ANEXO_PATH,
      buildCriarTranscricaoAnexoBody(payload),
      await getAuthConfig(),
    );

    return { ok: true };
  } catch (error: unknown) {
    console.log({ error });
    return failActionResult(error, fallbackMessage);
  }
};

const normalizeProcessarTranscricaoIACEAResponse = (
  data: unknown,
): Omit<Extract<ProcessarTranscricaoIACEAResult, { ok: true }>, 'ok'> | null => {
  const merged = mergeTranscricaoResponseRecord(data);
  if (!merged) return null;

  const idTranscricao = extractIdTranscricaoToken(merged);
  const codStatusTranscricao = pickStringByNormalizedKey(
    merged,
    'codstatustranscricao',
    'codStatusTranscricao',
    'CodStatusTranscricao',
  );

  if (!idTranscricao || !codStatusTranscricao) return null;

  return {
    idTranscricao,
    codStatusTranscricao,
    descrStatusTranscricao: pickStringByNormalizedKey(
      merged,
      'descrstatustranscricao',
      'descrStatusTranscricao',
      'DescrStatusTranscricao',
    ),
  };
};

export const processarTranscricaoIACEA = async (
  payload: ProcessarTranscricaoIACEARequest,
): Promise<ProcessarTranscricaoIACEAResult> => {
  const fallbackMessage =
    'Não foi possível processar a transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      PROCESSAR_TRANSCRICAO_IACEA_PATH,
      { IdTranscricao: payload.idTranscricao },
      await getAuthConfig(),
    );

    const accepted = normalizeProcessarTranscricaoIACEAResponse(response.data);
    if (accepted) {
      return { ok: true, ...accepted };
    }

    return {
      ok: true,
      idTranscricao: payload.idTranscricao,
      codStatusTranscricao: 'POS-IA',
    };
  } catch (error: unknown) {
    console.log({ error });
    return {
      ok: false,
      message: extractHttpErrorMessage(error, fallbackMessage),
    };
  }
};

const buildValidarDocumentoLegibilidadeIACEABody = (
  payload: ValidarDocumentoLegibilidadeIACEARequest,
) => ({
  Data: payload.data,
  ContentType: payload.contentType,
  NomeArquivo: payload.nomeArquivo,
});

const normalizeArquivoValido = (data: unknown): boolean | null => {
  const merged = mergeTranscricaoResponseRecord(data);
  if (!merged) return null;

  const raw =
    merged.arquivoValido
    ?? merged.ArquivoValido
    ?? Object.entries(merged).find(
      ([key]) => normalizeRecordKey(key) === 'arquivovalido',
    )?.[1];

  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }

  return null;
};

const normalizeCrm = (data: unknown): string | null => {
  const merged = mergeTranscricaoResponseRecord(data);
  if (!merged) return null;

  const raw =
    merged.crm
    ?? merged.Crm
    ?? Object.entries(merged).find(
      ([key]) => normalizeRecordKey(key) === 'crm',
    )?.[1];

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed || null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }

  return null;
};

export const validarDocumentoLegibilidadeIACEA = async (
  payload: ValidarDocumentoLegibilidadeIACEARequest,
): Promise<ValidarDocumentoLegibilidadeIACEAResult> => {
  const fallbackMessage = 'Erro ao validar arquivo.';

  try {
    const response = await apiClientes.post(
      VALIDAR_DOCUMENTO_LEGIBILIDADE_IACEA_PATH,
      buildValidarDocumentoLegibilidadeIACEABody(payload),
      await getAuthConfig(),
    );

    const arquivoValido = normalizeArquivoValido(response.data);
    if (arquivoValido === null) {
      return { ok: false, message: fallbackMessage };
    }

    return { ok: true, arquivoValido, crm: normalizeCrm(response.data) };
  } catch (error: unknown) {
    console.log({ error });
    return {
      ok: false,
      message: extractHttpErrorMessage(error, fallbackMessage),
    };
  }
};

export const recuperarTranscricaoHistorico = async (
  idTranscricao: TranscricaoResourceToken,
): Promise<TranscricaoServiceResult<TranscricaoHistoricoItem[]>> => {
  const fallbackMessage =
    'Não foi possível recuperar o histórico da transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      RECUPERAR_HISTORICO_PATH,
      buildConsultaTranscricaoBody(idTranscricao),
      await getAuthConfig(),
    );
    return {
      ok: true,
      data: normalizeHistoricoResponse(response.data),
    };
  } catch (error) {
    console.log({ error });
    return failServiceResult(error, fallbackMessage);
  }
};

export const recuperarTranscricaoHistoricoStatus = async (
  idTranscricao: TranscricaoResourceToken,
): Promise<TranscricaoServiceResult<TranscricaoHistoricoStatus>> => {
  const fallbackMessage =
    'Não foi possível recuperar o status da transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      RECUPERAR_HISTORICO_STATUS_PATH,
      buildConsultaTranscricaoBody(idTranscricao),
      await getAuthConfig(),
    );
    const status = normalizeHistoricoStatusResponse(response.data);
    if (!status) {
      return { ok: false, message: fallbackMessage };
    }
    return { ok: true, data: status };
  } catch (error) {
    console.log({ error });
    return failServiceResult(error, fallbackMessage);
  }
};

export const recuperarTranscricaoHistoricoSumario = async (
  idTranscricao: TranscricaoResourceToken,
): Promise<TranscricaoServiceResult<TranscricaoHistoricoStatus[]>> => {
  const fallbackMessage =
    'Não foi possível recuperar o histórico da transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      RECUPERAR_HISTORICO_SUMARIO_PATH,
      buildConsultaTranscricaoBody(idTranscricao),
      await getAuthConfig(),
    );
    return {
      ok: true,
      data: normalizeHistoricoSumarioResponse(response.data),
    };
  } catch (error) {
    console.log({ error });
    return failServiceResult(error, fallbackMessage);
  }
};

export const recuperarTranscricaoItem = async (
  idTranscricao: TranscricaoResourceToken,
): Promise<TranscricaoServiceResult<TranscricaoItemDetalhe[]>> => {
  const fallbackMessage =
    'Não foi possível recuperar os itens da transcrição. Tente novamente em instantes.';

  try {
    const response = await apiClientes.post(
      RECUPERAR_ITEM_PATH,
      buildConsultaTranscricaoBody(idTranscricao),
      await getAuthConfig(),
    );
    return {
      ok: true,
      data: normalizeItensResponse(response.data),
    };
  } catch (error) {
    console.log({ error });
    return failServiceResult(error, fallbackMessage);
  }
};

export const recuperarTranscricaoRascunhoAtivo = async (): Promise<TranscricaoResourceToken | null> => {
  try {
    const response = await apiClientes.post(
      OBTER_RASCUNHO_POR_BENEF_PATH,
      undefined,
      await getAuthConfig(),
    );

    return extractIdTranscricaoToken(response.data);
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 403 || axiosError.response?.status === 404) {
      return null;
    }
    console.log({ error });
    return null;
  }
};
